import { Link, useLocation } from 'react-router-dom';

// material-ui
import { Box, ButtonBase, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project imports
import navigation from 'menu-items';
import useLogout from 'hooks/useLogout';
import { mats, matsType } from 'themes/matsTokens';

// The menu is two levels here (Infrastructure / Orders, each with items), so
// each collapse becomes a labelled section rather than a clickable parent —
// the group label uses the redesign's eyebrow style.
const navSections = navigation.items.flatMap((group) =>
  (group.children || []).map((entry) =>
    entry.type === 'collapse' ? { title: entry.title, items: entry.children || [] } : { title: null, items: [entry] }
  )
);

const environmentName = process.env.REACT_APP_ENV_NAME || process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || '';

// ==============================|| SIDEBAR ||============================== //
// 204px fixed sidebar; collapses to a 60px icon rail below ~1280px.

const Sidebar = () => {
  const { logout } = useLogout();
  const { pathname } = useLocation();
  const collapsed = useMediaQuery('(max-width:1279.95px)');

  const width = collapsed ? mats.sidebarRailWidth : mats.sidebarWidth;

  const renderItem = (item) => {
    const active = pathname === item.url || pathname.startsWith(`${item.url}/`);
    const Icon = item.icon;

    const button = (
      <ButtonBase
        key={item.id}
        component={Link}
        to={item.url}
        sx={{
          width: '100%',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '9px 0' : '9px 18px',
          gap: '10px',
          fontFamily: mats.fontUi,
          fontSize: '13.5px',
          fontWeight: active ? 600 : 400,
          color: active ? mats.textPrimary : mats.textSecondary,
          backgroundColor: active ? mats.surfaceAccentTint : 'transparent',
          boxShadow: active ? `inset 2px 0 0 ${mats.accentPrimary}` : 'none',
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: active ? mats.surfaceAccentTint : mats.surfaceApp },
          '&:focus-visible': { outline: `2px solid ${mats.accentPrimary}`, outlineOffset: '-2px' }
        }}
      >
        {Icon && <Icon size={17} stroke={1.75} color={active ? mats.accentPrimary : mats.textMuted} />}
        {!collapsed && item.title}
      </ButtonBase>
    );

    return collapsed ? (
      <Tooltip key={item.id} title={item.title} placement="right">
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: mats.surfacePanel,
        borderRight: `1px solid ${mats.borderDefault}`,
        padding: '18px 0'
      }}
    >
      {/* brand */}
      <Box
        sx={{
          padding: collapsed ? '0 0 20px' : '0 18px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: '8px'
        }}
      >
        <Box
          component="img"
          src="/epex_icon.jpeg"
          alt="Trade Server"
          sx={{ width: 20, height: 20, borderRadius: '3px', display: 'block' }}
        />
        {!collapsed && (
          <Typography
            sx={{ fontFamily: mats.fontUi, fontSize: '14.5px', fontWeight: 600, letterSpacing: '-0.01em', color: mats.textPrimary }}
          >
            Trade Server
          </Typography>
        )}
      </Box>

      {/* nav */}
      <Box>
        {navSections.map((section, index) => (
          <Box key={section.title || index} sx={{ marginBottom: '10px' }}>
            {section.title && !collapsed && (
              <Typography sx={{ ...matsType.eyebrow, padding: '0 18px', marginBottom: '6px' }}>{section.title}</Typography>
            )}
            {section.items.map(renderItem)}
          </Box>
        ))}
      </Box>

      {/* environment */}
      {environmentName && !collapsed && (
        <Box sx={{ marginTop: '14px', borderTop: `1px solid ${mats.borderHairline}`, marginX: '18px', paddingTop: '16px' }}>
          <Typography sx={{ ...matsType.eyebrow, marginBottom: '8px' }}>Environment</Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              borderRadius: '3px',
              backgroundColor: mats.surfaceSunken,
              border: `1px solid ${mats.chipBorder}`,
              fontFamily: mats.fontUi,
              fontSize: '12.5px',
              fontWeight: 500,
              color: mats.textSecondary
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: mats.positive }} />
            {environmentName}
          </Box>
        </Box>
      )}

      <Box sx={{ flex: 1 }} />

      {/* user / logout */}
      <Box sx={{ padding: collapsed ? 0 : '0 18px', display: 'flex', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <ButtonBase
          onClick={logout}
          sx={{
            fontFamily: mats.fontUi,
            fontSize: '12.5px',
            color: mats.textFaint,
            padding: '4px 0',
            '&:hover': { color: mats.textSecondary },
            '&:focus-visible': { outline: `2px solid ${mats.accentPrimary}`, outlineOffset: '1px' }
          }}
        >
          Logout
        </ButtonBase>
      </Box>
    </Box>
  );
};

export default Sidebar;
